import { useTranslation } from "react-i18next";
import { Fragment } from "react/jsx-runtime";
import { WhiteBox, FoodsBadge, FoodsIcon } from "@foodpilot/foods";
import { Button, Grid2, Stack, Typography, useTheme } from "@mui/material";

import {
    ActionPlanOverview,
    QuestionnaireOverview,
    TargetOverview,
} from "src/api/client-api/company/overview/useOrganizationOverviewOneSsq.tsx";
import { useCompanyMetadata } from "src/context/Company/Metadata/useCompanyMetadata";
import { URLHandler } from "src/services/URLHandler";

import { LockedStrategy } from "./LockedStrategy";
import { EmptyStrategy } from "./EmptyStrategy";
import { ActionPlanSummaryLayout } from "./ActionPlanSummary";
import { HistogramBox } from "./HistogramBox";
import { DIAGNOSTIC_COLOR_INDEX } from ".";
import { Link } from "react-router";
import { ScoreService, ScoreWithVariation } from "src/services/ScoreService";
import { useResponsiveBreakpoints } from "src/utils/useResponsiveBreakpoints";
import { isAppInPDFMode } from "src/utils/rendering";
import { getSsqDisplayTitle } from "src/utils/ssqTitle";

type StrategyProps = {
    actionPlanTitle: string;
    scoreDetail: ScoreWithVariation | undefined;
    actionPlan: ActionPlanOverview | null | false;
    target: TargetOverview | null | false;
    diagnostic: QuestionnaireOverview | null;
    unit: string;
};
export const Strategy = (props: StrategyProps) => {
    const { actionPlanTitle, actionPlan, target, scoreDetail, diagnostic, unit } = props;
    const theme = useTheme();
    const { company } = useCompanyMetadata();

    const isLocked = diagnostic?.post?.isLocked === true;
    const doesExist = actionPlan !== null && actionPlan !== false;
    const datavizColors = theme.custom.dataviz.colorsWithExtra[DIAGNOSTIC_COLOR_INDEX];

    const ssqId = diagnostic === null ? -1 : diagnostic?.id;
    return (
        <WhiteBox border={`2px solid ${datavizColors.variant[700]}`}>
            <Stack padding={{ xs: theme.spacing(2), sm: theme.spacing(3) }} gap={theme.spacing(3)}>
                <Stack flexDirection={"row"} gap={theme.spacing(2)} alignItems={"center"}>
                    <FoodsBadge
                        size={8}
                        icon="trajectory"
                        boxProps={{
                            sx: {
                                color: datavizColors.variant[700],
                                backgroundColor: datavizColors.variant[100],
                                borderRadius: "8px",
                            },
                        }}
                    />
                    <Typography variant="h2">{actionPlanTitle}</Typography>
                </Stack>
                {doesExist ?
                    <InnerStrategy
                        ssqId={ssqId}
                        actionPlan={actionPlan}
                        target={target}
                        scoreDetail={scoreDetail}
                        unit={unit}
                    />
                : isLocked === false ?
                    <LockedStrategy questionnaireName={getSsqDisplayTitle(diagnostic)} />
                :   <EmptyStrategy
                        companyId={company.id}
                        ssqId={ssqId}
                        questionnaireName={getSsqDisplayTitle(diagnostic)}
                    />
                }
            </Stack>
        </WhiteBox>
    );
};

type InnerStrategyProps = {
    ssqId: number;
    actionPlan: ActionPlanOverview;
    target: TargetOverview | null | false;
    scoreDetail: ScoreWithVariation | undefined;
    unit: string;
};
const InnerStrategy = (props: InnerStrategyProps) => {
    const { actionPlan, target, scoreDetail, unit, ssqId } = props;
    const { company } = useCompanyMetadata();

    const { isTablet, isMobile } = useResponsiveBreakpoints();

    const theme = useTheme();
    const { t } = useTranslation();
    const isPdfMode = isAppInPDFMode();

    const engagedActions = actionPlan.completion.engaged;

    const _goalRemainder = ScoreService.substractScore(
        actionPlan.campaigns.end.value,
        target !== false ? (target?.value ?? null) : null,
    );

    // Default is -1 because we dont want to display if goal is achieved.
    const defaultRemainder = -1;
    const goalRemainder = _goalRemainder ?? defaultRemainder;
    return (
        <Fragment>
            <Stack flexDirection={"row"}>
                {isTablet || isMobile ?
                    <Stack gap={theme.spacing(2)} width={"100%"}>
                        <ActionPlanSummaryLayout engagedActions={engagedActions} />
                        <HistogramBox
                            ssqId={ssqId}
                            actionPlanCampaigns={actionPlan.campaigns}
                            unit={unit}
                            improvementType={scoreDetail?.improvement}
                        />
                    </Stack>
                :   <Grid2
                        container
                        width={"100%"}
                        spacing={theme.spacing(2)}
                        justifyItems={"stretch"}
                        alignItems={"stretch"}
                    >
                        <Grid2 size={6}>
                            <ActionPlanSummaryLayout engagedActions={engagedActions} />
                        </Grid2>
                        <Grid2 size={6}>
                            <HistogramBox
                                ssqId={ssqId}
                                actionPlanCampaigns={actionPlan.campaigns}
                                unit={unit}
                                improvementType={scoreDetail?.improvement}
                            />
                        </Grid2>
                    </Grid2>
                }
            </Stack>
            {target !== null && goalRemainder < 0 && (
                <Stack
                    flexDirection={"row"}
                    padding={theme.spacing(4)}
                    borderRadius={"12px"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                    sx={{
                        backgroundColor: theme.custom.dataviz.colorsWithExtra[12].variant[100],
                    }}
                >
                    <Stack flexDirection={"row"} gap={theme.spacing(0.5)}>
                        <Typography variant="big">
                            {t("action_plan.achieving_goal_completion_prefix", `Still`)}
                        </Typography>
                        <Typography variant="h4">{`${goalRemainder * -1} ${scoreDetail?.unit}`}</Typography>
                        <Typography variant="big">
                            {t(
                                "action_plan.achieving_goal_completion_suffix",
                                `to avoid in order to fullfill your objective`,
                            )}
                        </Typography>
                    </Stack>
                    {!isPdfMode && (
                        <Link to={URLHandler.company.actions.catalogList(company.id, ssqId)}>
                            <Button
                                variant="ternary"
                                startIcon={<FoodsIcon size={2} icon={"addRemove"} />}
                                sx={(theme) => ({
                                    ...theme.typography["big-bold"],
                                })}
                            >
                                {t("action_plan.go_to_catalog", "Add some actions from your catalog")}
                            </Button>
                        </Link>
                    )}
                </Stack>
            )}
        </Fragment>
    );
};
